import { useState } from 'react';
import { Check, Share } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { paths } from '@/lib/routes';
import { releaseArtworkImageUrl, setPageMeta } from '@/lib/pageMeta';
import { absoluteUrl, shareContent } from '@/lib/share';

type Props = {
  trackName: string;
  artistName: string;
  /** Catalog release id for the song / album (on-demand or radio source). */
  releaseId?: string;
  trackPosition?: number;
  albumName?: string;
  /** Release artwork path or absolute URL — used for OG preview meta. */
  artwork?: string;
  className?: string;
};

/** Share song / release from the now-playing drawer (link preview, no Live). */
export function NowPlayingShareButton({
  trackName,
  artistName,
  releaseId,
  trackPosition,
  albumName,
  artwork,
  className = 'now-playing-sheet-share',
}: Props) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const hasRelease = Boolean(releaseId?.trim());
  const hasTrack =
    hasRelease &&
    typeof trackPosition === 'number' &&
    Number.isFinite(trackPosition) &&
    trackPosition > 0;

  if (!hasRelease) return null;

  const image = releaseArtworkImageUrl(releaseId!, artwork);
  const artist = artistName.trim() || 'Zachary Gray';
  const shareNote = (name: string) =>
    `Check out ${name.trim()} by ${artist}`;

  const runShare = async (
    key: string,
    payload: { title: string; text?: string; url: string },
  ) => {
    const url = absoluteUrl(payload.url);
    setPageMeta({
      title: payload.title,
      description: payload.text,
      image,
      url,
    });
    const result = await shareContent(payload);
    if (result === 'copied') {
      setCopied(key);
      window.setTimeout(() => setCopied(null), 1600);
      return;
    }
    if (result === 'shared') setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={className} aria-label="Share">
          <Share size={16} strokeWidth={2.1} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="top"
        sideOffset={10}
        collisionPadding={16}
        className="now-playing-share-popover rounded-xl"
      >
        <p className="now-playing-share-title">Share</p>
        <div className="now-playing-share-actions">
          {hasTrack ? (
            <button
              type="button"
              className="now-playing-share-item"
              onClick={() =>
                void runShare('track', {
                  title: trackName,
                  text: shareNote(trackName),
                  url: paths.musicReleaseTrack(releaseId!, trackPosition!),
                })
              }
            >
              <span>Song</span>
              {copied === 'track' ? (
                <Check size={14} aria-hidden />
              ) : null}
            </button>
          ) : null}
          <button
            type="button"
            className="now-playing-share-item"
            onClick={() =>
              void runShare('release', {
                title: albumName || trackName,
                text: shareNote(albumName || trackName),
                url: paths.musicRelease(releaseId!),
              })
            }
          >
            <span>Release</span>
            {copied === 'release' ? (
              <Check size={14} aria-hidden />
            ) : null}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
